<div class="chatbot-container">
  <!-- Header -->
  <div style="display: flex; align-items: flex-start">
    <div>
      <div class="mds-headline--x-small mds-secondary__text-color">Ask me Anything</div>
      <div class="mds-text--small-normal" i18n>
        Ask questions about workorders, approvals, master data, users and more. Type your question or choose one of the sample questions.
      </div>
    </div>

    <div style="margin-left: auto; display: flex; gap: 8px">
      @if (messages.length > 1) {
        <mc-button
          appearance="primary"
          fit="small"
          variant="filled"
          label="New conversation"
          (click)="clearConversation()"
          (keydown.enter)="clearConversation()">
        </mc-button>
      }
    </div>
  </div>

  <!-- Example Questions Section -->
  @if (exampleQuestions.length > 0) {
    <div class="examples-section">
      @if (!isExamplesLoading) {
        <div class="examples-grid">
          @for (question of exampleQuestions; track trackByQuestionId($index, question)) {
            <mc-button
              [label]="question.question"
              variant="outlined"
              appearance="primary"
              fit="small"
              [disabled]="isLoading"
              (keydown.enter)="onExampleQuestionClick(question)"
              (click)="onExampleQuestionClick(question)">
            </mc-button>
          }
        </div>
      }
    </div>
  }

  <!-- Chat Container -->
  <div class="chat-container" #chatContainer>
    <div class="chat-messages">
      @for (message of messages; track trackByMessageId($index, message)) {
        <div class="message-wrapper" [class.user-message]="message.isUser" [class.bot-message]="!message.isUser">
          <div class="message-content">
            <div [class.message-avatar-user]="message.isUser" [class.message-avatar-bot]="!message.isUser">
              <mc-icon
                [icon]="message.isUser ? 'user-circle-solid' : 'chatbot'"
                [class.user-avatar]="message.isUser"
                [class.bot-avatar]="!message.isUser">
              </mc-icon>
            </div>

            <div class="message-bubble">
              <!-- Message text -->
              @if (!message.isLoading) {
                @if (!message.isUser && message.isNewContext && message !== messages[0]) {
                  <div class="new-context-badge">
                    <mc-icon icon="arrows-counter-clockwise" class="new-context-icon"></mc-icon>
                    <span>New context window</span>
                  </div>
                }
                <div class="message-text">
                  <markdown [data]="formatMessageContent(message.text)" class="markdown-content"></markdown>
                </div>
              }

              <!-- Loading indicator -->
              @if (message.isLoading) {
                <div class="message-loading">
                  <span class="thinking-label" i18n>Thinking</span>
                  <span class="thinking-dots">
                    <span class="dot dot-1">.</span>
                    <span class="dot dot-2">.</span>
                    <span class="dot dot-3">.</span>
                  </span>
                </div>
              }

              <!-- Chart / visualisation -->
              @if (!message.isUser && !message.isLoading && message.chartData) {
                <app-chart-renderer [chartData]="message.chartData"></app-chart-renderer>
              }

              <!-- Metadata panel for bot responses -->
              @if (!message.isUser && !message.isLoading && message.responseTimeMs) {
                <div class="message-metadata">
                  <div class="metadata-row">
                    <span class="metadata-text">Response time: {{ message.responseTimeMs | number: '1.0-0' : 'en-US' }}ms</span>
                  </div>
                  @if (message.tokensUsed) {
                    <div class="metadata-row">
                      <span class="metadata-text">Tokens used: {{ message.tokensUsed | number }}</span>
                    </div>
                  }
                  @if (message.nlbiMetadata?.row_count !== undefined) {
                    <div class="metadata-row">
                      <span class="metadata-text">Rows returned: {{ message.nlbiMetadata!.row_count | number }}</span>
                    </div>
                  }
                  @if (message.nlbiMetadata?.query_source) {
                    <div class="metadata-row">
                      <span class="metadata-text">Source: {{ message.nlbiMetadata!.query_source }}</span>
                    </div>
                  }

                  <!-- SQL Query -->
                  @if (message.nlbiMetadata?.sql) {
                    <div class="sql-query-section">
                      <button class="sql-toggle-btn" (click)="toggleSqlQuery(message.id)">
                        {{ showSqlQuery[message.id] ? 'Hide SQL' : 'View SQL' }}
                      </button>
                      @if (showSqlQuery[message.id]) {
                        <pre class="sql-code">{{ message.nlbiMetadata!.sql }}</pre>
                      }
                    </div>
                  }
                </div>
              }

              <!-- Star rating section for bot messages -->
              @if (!message.isUser && !message.isLoading && message.turnId) {
                <div class="rating-section">
                  @if (!message.ratingSubmitted) {
                    <span class="rating-label">Rate this answer:</span>
                    <div class="star-rating" (mouseleave)="clearHoverRating(message)">
                      @for (star of stars; track star) {
                        <mc-icon
                          [icon]="getStarIcon(message, star)"
                          class="star-icon"
                          [class.star-active]="star <= (message.pendingRating ?? 0)"
                          (mouseenter)="setHoverRating(message, star)"
                          (click)="selectRating(message, star)"
                          (keydown.enter)="selectRating(message, star)">
                        </mc-icon>
                      }
                    </div>
                    @if (message.rating) {
                      <mc-button
                        fit="small"
                        variant="outline"
                        label="Submit"
                        (click)="submitRating(message)"
                        (keydown.enter)="submitRating(message)">
                      </mc-button>
                      <div class="rating-comment-row">
                        <input
                          type="text"
                          class="rating-comment-input"
                          placeholder="Optional comment..."
                          [(ngModel)]="message.ratingComment"
                          maxlength="300" />
                      </div>
                    }
                  } @else {
                    <div class="rating-submitted">
                      <div class="star-rating star-rating--readonly">
                        @for (star of stars; track star) {
                          <mc-icon
                            [icon]="getStarIcon(message, star)"
                            class="star-icon star-icon--small"
                            [class.star-active]="star <= (message.rating ?? 0)">
                          </mc-icon>
                        }
                      </div>
                      <span class="rating-thanks">Thanks for your feedback!</span>
                    </div>
                  }
                </div>
              }

              <!-- Export button (hidden by default, shown on hover via CSS) -->
              @if (!message.isUser && !message.isLoading) {
                <div class="export-btn-row">
                  <mc-button variant="outline" fit="small" icon="tray-arrow-down" (click)="exportMessageToPdf(message)"></mc-button>
                </div>
              }
            </div>
          </div>
        </div>
      }
    </div>
  </div>

  <!-- Input Section -->
  <div class="input-section">
    <form [formGroup]="chatForm" (ngSubmit)="onSubmit()" class="chat-form">
      <div class="input-container">
        <mc-input
          type="text"
          ngDefaultControl
          hiddenlabel
          formControlName="message"
          placeholder="Type your question here..."
          [disabled]="isLoading"
          class="message-input"
          (keypress)="onKeyPress($event)"
          i18n-placeholder>
          <span slot="hint">This content is AI-generated and may be incomplete or inaccurate</span>
        </mc-input>

        <mc-button class="m-l-8" hiddenlabel [disabled]="chatForm.invalid || isLoading" icon="paper-plane"> </mc-button>
      </div>
    </form>
  </div>
</div>
